import { motion } from "framer-motion";
import { Sparkles, Wrench, Check } from "lucide-react";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

const CHAPTERS = [
  {
    no: "01",
    icon: Sparkles,
    title: "Deep & Detailed",
    tagline: "Sparkling Every Time",
    items: [
      { h: "Residential", d: "Weekly, bi-weekly, monthly upkeep" },
      { h: "Deep Cleaning", d: "Baseboards, tile & grout, interior windows / doors" },
      { h: "Specialty", d: "Move-in / move-out, post construction, inside appliances" },
      { h: "Extras Locals Love", d: "Garage cleaning & organization, junk removal" },
    ],
    footnote: "Eco-Friendly Products Available · Satisfaction Guaranteed",
  },
  {
    no: "02",
    icon: Wrench,
    title: "Handyman Services",
    tagline: "We Complete The List",
    items: [
      { h: "Minor Repairs", d: "Plumbing, drywall, doors, tiles — stop the drip, crack & squeak" },
      { h: "Installations", d: "TV mounting, pictures, shelving, ceiling fans, light fixtures" },
      { h: "General Maintenance", d: "Pressure washing, painting, furniture assembly, flooring, carpentry" },
    ],
    chips: ["Plumbing Fixes", "Drywall Patch", "TV Mounting", "Shelving", "Pressure Washing", "Painting", "Furniture Assembly", "Flooring"],
    footnote: "Same Crew · No Subcontractors · Treasure Coast Trusted",
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="relative bg-[#FDFBF7] pt-28 lg:pt-36 pb-16 lg:pb-20">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="max-w-2xl mb-20">
          <span className="font-jet text-[11px] uppercase tracking-[0.3em] text-[#993B19]">Our Services</span>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#0F172A]">
            One Team. Every Job <em className="italic text-[#D96B43]">Done Right.</em>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#475569] leading-relaxed">
            Cleaning that shines + handyman that fixes. No job too small on your honey-do list.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {CHAPTERS.map((ch, ci) => (
            <motion.article
              key={ch.no}
              data-testid={`service-chapter-${ch.no}`}
              variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: ci * 0.12 }}
              className="group relative bg-white rounded-3xl border border-[#D96B43]/15 p-8 lg:p-10 hover:border-[#D96B43]/50 hover:shadow-2xl hover:shadow-[#D96B43]/10 transition-all duration-500"
            >
              <div className="flex items-start justify-end mb-8">
                <span className="w-12 h-12 rounded-full bg-[#F5EBE6] flex items-center justify-center text-[#993B19] group-hover:bg-[#0F172A] group-hover:text-[#FDFBF7] transition-colors duration-500">
                  <ch.icon size={20} />
                </span>
              </div>

              <h3 className="font-display text-3xl lg:text-4xl text-[#0F172A] tracking-tight">{ch.title}</h3>
              <p className="mt-1 font-jet text-[11px] uppercase tracking-[0.25em] text-[#D96B43]">{ch.tagline}</p>

              <ul className="mt-8 space-y-5">
                {ch.items.map((it) => (
                  <li key={it.h} className="flex gap-3">
                    <Check size={17} className="mt-1 shrink-0 text-[#D96B43]" strokeWidth={2.5} />
                    <div>
                      <span className="font-semibold text-[#0F172A]">{it.h}</span>
                      <span className="block text-sm text-[#64748B] mt-0.5">{it.d}</span>
                    </div>
                  </li>
                ))}
              </ul>

              {ch.chips && (
                <div className="mt-7 flex flex-wrap gap-2">
                  {ch.chips.map((c) => (
                    <span key={c} className="font-jet text-[10px] uppercase tracking-[0.12em] px-3 py-1.5 rounded-full bg-[#F5EBE6] text-[#993B19]">
                      {c}
                    </span>
                  ))}
                </div>
              )}

              <p className="mt-8 pt-6 border-t border-[#0F172A]/8 font-jet text-[10px] uppercase tracking-[0.2em] text-[#64748B]">
                {ch.footnote}
              </p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
